import { FormEventHandler, useEffect, useState } from "react";
import { Form } from "@remix-run/react";
import { RoundedCta } from "~/components/ctas";
import { CaptureConfig, Mapping } from "../newsletter/formCapture.types";
import { MultiselectOption, MultiselectOptions } from "./MultiselectOptions";
import FieldRow50 from "./FieldRow50";
import SubmissionComplete from "./SubmissionComplete";

type HtmlStringFormCaptureProps = {
  html: string;
};

const inputStyle =
  "body border-b-1 col-span-4 w-full border border-black rounded-sm px-4 py-2 mt-2 placeholder:placeholder-gray-400 focus:outline-1 lg:col-start-2";

const multiselectOptions: MultiselectOption[] = [
  {
    value: "dairyree",
    label: "Dairy Free",
    dataverseValue: "928430000",
  },
  {
    value: "glutenfree",
    label: "Gluten Free",
    dataverseValue: "928430001",
  },
  {
    value: "halal",
    label: "Halal",
    dataverseValue: "928430002",
  },
  {
    value: "kosher",
    label: "Kosher",
    dataverseValue: "928430003",
  },
  {
    value: "nut_allergy",
    label: "Nut allergy",
    dataverseValue: "928430004",
  },
  {
    value: "other",
    label: "Other",
    dataverseValue: "928430005",
  },
  {
    value: "shellfish",
    label: "Shellfish",
    dataverseValue: "928430006",
  },
  {
    value: "vegetarian",
    label: "Vegetarian",
    dataverseValue: "928430007",
  },
];

const dietaryOptions = {
  FormFieldName: "embedform_dietary_options",
  DataverseFieldName: "tbi_dietaryoptions",
  DataverseFieldValue: multiselectOptions.map((option) => ({
    FormValue: option.value,
    DataverseValue: option.dataverseValue,
  })),
};

// Define the mappings
const mappings: Mapping[] = [
  {
    FormFieldName: "embedform_first_name",
    DataverseFieldName: "firstname",
  },
  {
    FormFieldName: "embedform_last_name",
    DataverseFieldName: "lastname",
  },
  {
    FormFieldName: "embedform_organisation",
    DataverseFieldName: "adx_organizationname",
  },
  {
    FormFieldName: "embedform_job_title",
    DataverseFieldName: "jobtitle",
  },
  {
    FormFieldName: "embedform_email",
    DataverseFieldName: "emailaddress1",
  },
  dietaryOptions,
  {
    FormFieldName: "embedform_innovation_perspective",
    DataverseFieldName: "tbi_wheredoyouofferauniqueperspectiveonpolicy",
  },
  {
    FormFieldName: "embedform_innovation_solution",
    DataverseFieldName: "tbi_ifrelevantwhattechnologysolutionorproduct",
  },
];

const captureConfig: CaptureConfig = {
  FormId: "1a8ede40-552b-f011-8c4d-7c1e5265d8ca",
  FormApiUrl:
    "https://public-gbr.mkt.dynamics.com/api/v1.0/orgs/7a2dad3e-a1b3-4828-98dd-e6bd8c664cde/landingpageforms",
};

export default function Event({ html }: HtmlStringFormCaptureProps) {
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit: FormEventHandler = async (e) => {
    e.preventDefault();
    setSubmitted(true);
    if (!window.d365mktformcapture) {
      return;
    }

    const form = e.currentTarget as HTMLFormElement;
    const serializedForm = window.d365mktformcapture.serializeForm(
      form,
      mappings
    );
    const payload = serializedForm.SerializedForm.build();
    try {
      await window.d365mktformcapture.submitForm(captureConfig, payload);
    } catch (e) {
      console.log(e, "error");
    }
  };

  useEffect(() => {
    if (window.d365mktformcapture) {
      return; // Already loaded
    }
    const script = document.createElement("script");
    script.src =
      "https://cxppusa1formui01cdnsa01-endpoint.azureedge.net/gbr/FormCapture/FormCapture.bundle.js";
    script.async = true;
    document.body.appendChild(script);
  }, []);

  if (submitted) {
    return <SubmissionComplete />;
  }

  return (
    <Form
      className="col-span-full mt-8 text-black lg:col-span-8 lg:col-start-3"
      onSubmit={handleSubmit}
      id="event"
    >
      <FieldRow50
        options={[
          {
            label: "First Name",
            value: "embedform_first_name",
            isRequired: true,
          },
          {
            label: "Last Name",
            value: "embedform_last_name",
            isRequired: true,
          },
        ]}
        inputStyle={inputStyle}
      />
      <FieldRow50
        options={[
          {
            label: "Job Title",
            value: "embedform_job_title",
          },
          {
            label: "Organisation Name",
            value: "embedform_organisation",
          },
        ]}
        inputStyle={inputStyle}
        rowStyle="pt-6"
      />

      <div className="pt-6">
        <label htmlFor="embedform_email" className="block font-bold">
          Email *
        </label>
        <input
          type={"email"}
          id="embedform_email"
          name="embedform_email"
          className={inputStyle}
          placeholder="Email"
          required
        />
      </div>
      <div className="pt-6">
        <div className="block font-bold">Dietary Options</div>
        <MultiselectOptions
          options={multiselectOptions}
          formFieldName={dietaryOptions.FormFieldName}
          isRequired
        />
      </div>
      <RoundedCta
        kind="black"
        label="Submit"
        type="submit"
        className="mt-6 px-6 py-2"
      />
    </Form>
  );
}
